import React from "react";
import { Typography } from "antd";
import DistributionChart from "./DistributionChart";

const { Title } = Typography;

const StudentCurriculums = ({ streams = [], report_period }) => {
  if (!streams.length) return null;

  return (
    <div
      style={{
        width: "210mm",
        margin: "0 auto",
        fontFamily: "Arial, sans-serif",
        color: "#333",
        boxSizing: "border-box",
      }}
    >
      {streams.map((stream) =>
        stream.learning_activities?.map((activity) =>
          activity.curriculums?.map((item, index) => {
            const {
              curriculum = {},
              runtime_competency_rate = 0,
              homework_rate = 0,
              parent_engagement_rate = 0,
              pending_homework = 0,
              submitted_homework = 0,
              verified_homework = 0,
              total_homework = 0,
              profile_risk = "",
              progress_status = "",
              status = "",
              runtime_grade = "",
              grade_distribution = {},
              overall_summary = {},
              all_attempts = []
            } = item;

            const categoryTotal = Object.fromEntries(
              Object.entries(overall_summary).map(([key, values]) => [
                key,
                Object.values(values).reduce((sum, value) => sum + value, 0)
              ])
            );

            const result = Object.values(
              all_attempts.reduce((acc, attempt) => {
                attempt.summary.forEach((item) => {
                  const { _id, name } = item.observation;
            
                  if (!acc[_id]) {
                    acc[_id] = {
                      _id,
                      observation_name: name,
                      observed_count: 0,
                      performance_sum: 0,
                      total_observations: 0,
                    };
                  }
            
                  acc[_id].observed_count += item.observed_count;
                  acc[_id].performance_sum += item.performance;
                  acc[_id].total_observations += 1;
                });
            
                return acc;
              }, {})
            ).map((item) => ({
              ...item,
              average_performance:
                item.total_observations > 0
                  ? item.performance_sum / item.total_observations
                  : 0,
            }));

            const normalizedGrade =
              runtime_grade === "E" || runtime_grade === "U"
                ? "D"
                : runtime_grade;

            return (
              <div
                key={item._id || index}
                className="pdf-page curriculum-block"
                style={{
                  width: "210mm",
                  minHeight: "297mm",
                  padding: "8mm",
                  margin: "0 auto 15px auto",
                  background: "#fff",
                  border: "1px solid #ccc",
                  boxSizing: "border-box",
                  display: "block",
                  pageBreakAfter: "always",
                  pageBreakBefore: "always",
                  pageBreakInside: "avoid",
                  breakInside: "avoid-page",
                  overflow: "visible",

                }}
              >
                <div style={{
                  display: "flex", flexDirection: "column", gap: "10px", marginBottom: "10px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "10px",
                  padding: "8px",
                  background: "#f9f9f9",
                }}>
                  <Title level={3} style={{ color: "#50B6F2", margin: 0 }}>
                    {activity.name}
                  </Title>


                  <div
                    style={{
                      background: "#fff",
                      borderRadius: "8px",
                      padding: "8px",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      breakInside: "auto",
                      pageBreakInside: "auto",
                      overflow: "visible",
                    }}
                  >
                    <Title level={4} style={{ color: "#50B6F2", margin: "0 0 6px 0" }}>
                      {curriculum?.name || "Curriculum"}{" "}
                      {curriculum?.nominal_year_level ? `(Level ${curriculum.nominal_year_level})` : ""}
                    </Title>

                    <div
                      style={{
                        background: "#f2f2f2",
                        padding: "10px",
                        fontSize: "12px",
                        breakInside: "avoid",
                        pageBreakInside: "avoid",
                      }}
                    >
                      <b style={{ color: "#50B6F2" }}>Subject Outline</b>
                      <p style={{ textAlign: "justify" }}>
                        In <b>{report_period}</b>, students improved various skills related to <b>{curriculum?.name}</b> through engaging lessons and assessments.
                      </p>
                    </div>

                    <div style={{ display: "flex", gap: "20px", breakInside: "avoid", pageBreakInside: "avoid" }}>
                      <div style={{ flex: 1, padding: "4px" }}>
                        <h4 style={{ marginBottom: "5px", fontWeight: "bold" }}>GRADE</h4>
                        <Title level={1} style={{ color: "#50B6F2", margin: 0 }}>
                          {normalizedGrade || "-"}
                        </Title>
                      </div>
                      <div style={{ flex: 2, padding: "4px" }}>
                        <h4 style={{ marginBottom: "2px", fontWeight: "bold" }}>DISTRIBUTION</h4>
                        <DistributionChart chartData={grade_distribution} studentGrade={normalizedGrade} />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", breakInside: "avoid", pageBreakInside: "avoid" }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ marginBottom: "2px", fontWeight: "bold" }}>Academic Performance</h4>
                        <table style={{ width: "100%", fontSize: "12px" }}>
                          <tbody>
                            <tr><td>1. Competency Rate</td><td>{runtime_competency_rate.toFixed(2)}%</td></tr>
                            <tr><td>2. Homework Rate</td><td>{homework_rate}%</td></tr>
                            <tr><td>3. Parent Engagement Rate</td><td>{parent_engagement_rate}%</td></tr>
                            <tr><td>4. Profile Risk</td><td style={{ color: profile_risk === "high" ? "#FF1A1A" : profile_risk === "medium" ? "#F3CC00" : "#00CE08", fontWeight: "bold" }}>{profile_risk && profile_risk.charAt(0).toUpperCase() + profile_risk.slice(1).toLowerCase()}</td></tr>
                            <tr><td>5. Progress Status</td><td>{progress_status}</td></tr>
                            <tr><td>6. Curriculum Status</td><td>{status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : "-"}</td></tr>
                          </tbody>
                        </table>
                      </div>

                      <div style={{ flex: 1 }}>
                        <h4 style={{ marginBottom: "2px", fontWeight: "bold" }}>Homework Progress</h4>
                        <table style={{ width: "100%", fontSize: "12px" }}>
                          <tbody>
                            <tr><td>1. Submitted Homework</td><td>{submitted_homework}</td></tr>
                            <tr><td>2. Verified Homework</td><td>{verified_homework}</td></tr>
                            <tr><td>3. Pending Homework</td><td>{pending_homework}</td></tr>
                            <tr><td>4. Total Homework</td><td>{total_homework}</td></tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {result.length > 0 && (
                      <div
                        style={{
                          marginTop: "10px",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          padding: "10px",
                          background: "#fff",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                          breakInside: "auto",
                          pageBreakInside: "auto",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center",
                            marginBottom: "8px",
                          }}
                        >
                          <h4
                            style={{
                              margin: 0,
                              color: "#50B6F2",
                            }}
                          >
                            Observations:
                          </h4>
                        </div>
                        {result.map(
                          (item, idx) => {
                            const { observation_name, observed_count, average_performance } = item;
                            
                            // Performance is out of 10
                            const performancePercentage = (average_performance / 10) * 100;

                            return (
                              <div
                                key={idx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  fontSize: "12px",
                                  marginBottom: "6px",
                                  gap: "6px",
                                }}
                              >
                                {/* Observation name + observed count */}
                                <div
                                  style={{
                                    width: "150px",
                                    flexShrink: 0,
                                    fontWeight: "500",
                                  }}
                                >
                                  {observation_name} ({observed_count})
                                </div>

                                {/* Separator */}
                                <div
                                  style={{
                                    width: "1px",
                                    background: "#ccc",
                                    height: "14px",
                                    flexShrink: 0,
                                  }}
                                />

                                {/* Progress bar */}
                                <div
                                  style={{
                                    flex: 1,
                                    height: "12px",
                                    background: "#f0f0f0",
                                    borderRadius: "2px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${performancePercentage}%`,
                                      background: "#50B6F2",
                                      height: "100%",
                                      borderRadius: "2px",
                                    }}
                                  />
                                </div>

                                {/* Percentage */}
                                <div
                                  style={{
                                    minWidth: "42px",
                                    textAlign: "right",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {performancePercentage.toFixed(0)}%
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )
      )}
    </div>
  );
};

export default StudentCurriculums;